/*******************************************************
 * useGlobalSettings Hook
 * 
 * Read-only access to global settings stored in the AppDB.SettingsGlobal
 * table with automatic type conversion.
 * 
 * Key Features:
 * - Read-only access to global settings via AppContext
 * - Automatic value conversion:
 *   - '0'/'1' -> boolean
 *   - numeric strings -> number
 *   - everything else -> string
 * 
 * Usage Example:
 * ```tsx
 * const { getSetting } = useGlobalSettings();
 * const debounceMs = getSetting('Debounce_Default'); // number
 * ```
 ********************************************************/
//########## IMPORTS ##########
// #region IMPORTS
import { useCallback, useMemo } from 'react';
import { useApp } from '@/src/contexts/AppContext';
import { type TableModel } from '@/src/db/models/EntityRelationships.types';
import { type APIGetSuccessResponse } from '@/src/scripts/libs/typesLib';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
type GlobalSettingRecord = TableModel<'SettingsGlobal'>;
//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS

// #endregion


/**
 * useGlobalSettings - Provides read-only access to global settings with automatic type conversion
 */
export default function useGlobalSettings() {
    //########## APIClient & Context ##########
    // #region APIClient & Context
    const { App, loading: appContextLoading, error: appContextError } = useApp();

    const settingsResponse = App.SettingsGlobal as APIGetSuccessResponse<GlobalSettingRecord> | undefined;
    const settingsData = useMemo<GlobalSettingRecord[]>(() => settingsResponse?.data ?? [], [settingsResponse?.data]);
    // #endregion

    //########## CALLBACKS ##########
    // #region CALLBACKS
    const processValue = useCallback((raw: string): boolean | number | string => {
        if (raw === '0') return false;
        if (raw === '1') return true;
        const asNumber = Number(raw);
        if (!Number.isNaN(asNumber) && raw.trim() !== '') return asNumber;
        return raw;
    }, []);

    const getSetting = useCallback((label: string): boolean | number | string | undefined => {
        const setting = settingsData.find((s) => s.Label === label);
        if (!setting) return undefined;
        return processValue(setting.Value);
    }, [settingsData, processValue]);
    // #endregion

    //########## RETURN ##########
    // #region RETURN
    return {
        getSetting,
        isSettingsLoading: appContextLoading,
        isSettingsError: appContextError
    };
    // #endregion
}

